import React from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Lock, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useBilling } from "@/context/BillingContext";

const TITLES = {
  limit_clients: "Hai raggiunto il limite di clienti",
  limit_contracts: "Hai raggiunto il limite di contratti",
  trial_send: "Invio prove non disponibile",
  trial_share: "Condivisione prove non disponibile",
  module_timeline: "Timeline non disponibile",
  module_mappa: "Mappa non disponibile",
  module_team: "Team non disponibile",
  module_ai_manager: "AI Manager™ riservato",
  generic: "Sblocca tutte le funzionalità",
};

const SUGGESTED_PLAN = {
  limit_clients: "basic",
  limit_contracts: "basic",
  trial_send: "basic",
  trial_share: "basic",
  module_timeline: "pro",
  module_mappa: "pro",
  module_team: "pro",
  module_ai_manager: "ai_manager",
  generic: "pro",
};

const PLAN_LABELS = {
  basic: "Basic",
  pro: "Pro",
  ai_manager: "AI Manager™",
};

export default function UpgradeModal() {
  const { upgradeContext, closeUpgrade } = useBilling();
  const navigate = useNavigate();
  const open = !!upgradeContext;

  if (!open) return null;
  const reason = upgradeContext?.reason || "generic";
  const title = TITLES[reason] || TITLES.generic;
  const suggested = upgradeContext?.requiredPlan || SUGGESTED_PLAN[reason] || "pro";
  const suggestedLabel = PLAN_LABELS[suggested] || "Pro";

  const goPlans = () => {
    closeUpgrade();
    navigate("/app/plans");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeUpgrade()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="upgrade-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              {suggested === "ai_manager" ? (
                <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
              ) : (
                <Lock className="w-5 h-5 text-primary" strokeWidth={1.5} />
              )}
            </span>
            <DialogTitle className="font-serif-heading text-2xl text-left">
              {title}
            </DialogTitle>
          </div>
        </DialogHeader>

        <p className="text-sm text-neutral-600 leading-relaxed">
          {upgradeContext?.message ||
            "Passa al piano superiore per sbloccare questa funzionalità."}
        </p>

        <div className="bg-secondary/40 border border-border rounded-sm p-4 mt-2 flex items-start gap-3">
          <Crown className="w-4 h-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
          <div className="text-sm">
            <div className="font-medium">
              Piano suggerito: <span className="text-primary">{suggestedLabel}</span>
            </div>
            <div className="text-xs text-neutral-500 mt-0.5">
              Confronta tutti i piani e scegli quello più adatto al tuo business.
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-2">
          <Button
            variant="outline"
            className="flex-1 rounded-sm"
            onClick={closeUpgrade}
            data-testid="upgrade-modal-close"
          >
            Annulla
          </Button>
          <Button
            className="flex-1 rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground"
            onClick={goPlans}
            data-testid="upgrade-modal-cta"
          >
            <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} /> Scopri i piani
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
